import type { VatLine } from '../vat';

import { supabase } from '../supabase';
import { storedUtm } from '../attribution';
import { DeliveryMethod, Order, PaymentMethod, User, UserRole } from '../../types';
import { safeDate } from './shared';
import { fetchAll } from './fetchAll';
import type { ShippingQuote } from '../shipping';
import { apiFetch } from '../apiFetch';

/** Det kunden ser på tacksidan — beloppen kommer från servern, inte klienten. */
export interface OrderReceipt {
    orderId: string;
    orderNumber: string;
    deliveryMethod: DeliveryMethod;
    paymentMethod?: PaymentMethod;
    /** Säljarens Swishnummer när kunden valt Swish till säljaren. */
    sellerSwish?: string | null;
    /** Varorna. Frakten ligger i shippingAmount. */
    totalAmount: number | null;
    shippingAmount: number;
    shippingWeightGrams: number | null;
    /** Bara vid hemleverans: Stripes nyckel för betalfältet i kassan. */
    clientSecret?: string;
    items: {
        name: string; variantName: string | null; quantity: number; price: number;
        /** Paket: vad det innehåller. */ contents?: string | null;
        /** Paket: delarna med ordinarie pris, andel av paketpriset och moms, och paketrabatten. */
        bundle?: { parts: { label: string; ordinary: number; allocated: number; vatRate: number | null }[]; discount: number } | null;
    }[];
    /** Moms per sats, räknad av databasen (frakten inräknad). */
    vatBreakdown?: VatLine[];
    sellerName: string;
    customerEmail: string;
    confirmationSent: boolean;
    // Från kassan, för tacksidan (skickas inte av servern):
    customerFirstName?: string;
    shippingAddress?: string;
    campaignEnd?: number;
    className?: string;
    /** Hemleverans: Stripes svar. "processing" = t.ex. Klarna som bekräftas strax. */
    paymentState?: 'succeeded' | 'processing';
}

/** Det kassan skickar utöver ordern: leveranssätt och adress för hemleverans. */
export interface CheckoutDelivery {
    method: DeliveryMethod;
    /** Hur kunden betalar. Gruppens val kontrolleras i databasen. */
    paymentMethod?: PaymentMethod;
    postalCode?: string;
    city?: string;
}

/**
 * Skapar ordern via api/orders/create-order.ts. Priser och frakt räknas i
 * databasen. Vid hemleverans svarar servern med Stripes clientSecret, och
 * ordern syns först när betalningen är klar.
 */
export const createOrder = async (order: Order, delivery: CheckoutDelivery = { method: 'PICKUP' }, opts: { offerDeclined?: boolean } = {}): Promise<OrderReceipt> => {
    // Transform items to the format expected by the API route/trigger
    const simpleItems = order.items.map(i => ({
        productId: i.productId,
        variantId: i.variantId,
        variantName: i.variantName, // Pass meta for receipt
        quantity: i.quantity,
        ...(i.choices && Object.keys(i.choices).length > 0 ? { choices: i.choices } : {})
    }));

    const payload = {
        studentId: order.studentId,
        items: simpleItems,
        marketingConsent: order.marketingConsent,
        // Kunden tackade nej till erbjudandemejlet. Servern sparar det på e-postadressen.
        offerDeclined: opts.offerDeclined === true,
        // Varifrån kunden kom (QR på säljkort, reklamblad …), om besöket hade en UTM-kod.
        utm: storedUtm(),
        deliveryMethod: delivery.method,
        paymentMethod: delivery.paymentMethod || 'SELLER',
        customer: {
            name: order.customerName,
            email: order.customerEmail,
            phone: order.customerPhone,
            address: order.customerAddress,
            postalCode: delivery.postalCode,
            city: delivery.city
        }
    };

    let response: Response;
    try {
        response = await fetch('/api/orders/create-order', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
    } catch (e) {
        throw new Error("Kunde inte nå servern. Kontrollera din anslutning.");
    }

    const data = await response.json().catch(() => null);

    if (!response.ok) {
        console.error("Order Creation Failed:", data);
        throw new Error(data?.error || "Ett fel uppstod vid beställningen. Kontrollera uppgifterna.");
    }

    return data as OrderReceipt;
};

const ms = (v: string | null | undefined) => (v ? new Date(v).getTime() : undefined);

const optNum = (v: unknown): number | undefined => (v === null || v === undefined ? undefined : Number(v));

/** En orderrad från databasen (tabellen eller get_customer_orders_secure). */
export const mapOrderRow = (o: any): Order => ({
    id: o.id,
    classId: o.class_id,
    studentId: o.student_id,
    studentName: o.student_name_snapshot || o.student_name || 'Okänd',
    classNameSnapshot: o.class_name_snapshot,
    customerName: o.customer_name,
    customerEmail: o.customer_email,
    customerPhone: o.customer_phone,
    customerAddress: o.customer_address,
    totalAmount: Number(o.total_amount),
    status: o.status,
    paymentStatus: o.payment_status || 'UNPAID',
    deliveryMethod: o.delivery_method === 'HOME' ? 'HOME' : 'PICKUP',
    paymentMethod: o.payment_method === 'STRIPE' || o.payment_method === 'SWISH' ? o.payment_method : 'SELLER',
    shippingAmount: Number(o.shipping_amount || 0),
    shippingWeightGrams: o.shipping_weight_grams ?? undefined,
    shippingPostalCode: o.shipping_postal_code || undefined,
    shippingCity: o.shipping_city || undefined,
    shippingQuote: o.shipping_quote ?? null,
    shippingWeighedGrams: optNum(o.shipping_weighed_grams),
    shippingLengthCm: optNum(o.shipping_length_cm),
    shippingWidthCm: optNum(o.shipping_width_cm),
    shippingHeightCm: optNum(o.shipping_height_cm),
    paidAt: ms(o.paid_at),
    shippedAt: ms(o.shipped_at),
    trackingNumber: o.tracking_number || undefined,
    refundedAt: ms(o.refunded_at),
    vatBreakdown: Array.isArray(o.vat_breakdown) ? o.vat_breakdown.map((l: any) => ({ rate: Number(l.rate), gross: Number(l.gross), shipping: Number(l.shipping || 0), vat: Number(l.vat), net: Number(l.net) })) : undefined,
    timestamp: safeDate(o.timestamp, o.created_at),
    items: o.items,
    deletedAt: o.deleted_at ? new Date(o.deleted_at).getTime() : undefined,
    invoiceId: o.invoice_id || undefined,
    marketingConsent: o.marketing_consent,
    utm: o.utm ?? null
});

export const getOrdersForUser = async (user: User): Promise<Order[]> => {
    let scope: (q: any) => any;

    if (user.role === UserRole.STUDENT) {
        scope = q => q.eq('student_id', user.id);
    } else if (user.role === UserRole.TEACHER && user.assignedClassId) {
        // Fetch all orders for this class ID (Robust way)
        // OR fetch all orders for students currently in this class (Legacy support)
        const { data: students } = await supabase.from('profiles').select('id').eq('class_id', user.assignedClassId);
        const ids = (students || []).map(s => s.id);
        const classId = user.assignedClassId;

        // This query says: Get orders explicitly linked to this class OR created by students currently in this class
        // (The latter handles legacy orders before class_id column existed)
        scope = ids.length > 0
            ? q => q.or(`class_id.eq.${classId},student_id.in.(${ids.join(',')})`)
            : q => q.eq('class_id', classId);
    } else if (user.role === UserRole.ADMIN) {
        scope = q => q;
    } else {
        return [];
    }

    let data: any[];
    try {
        // Admin ser alla ordrar: fler än 1 000 hämtas sida för sida.
        data = await fetchAll((from, to) => scope(supabase.from('orders').select('*')).order('id').range(from, to));
    } catch (error) {
        console.error("Order fetch error:", error);
        return [];
    }

    // Obetalda hemleveranser (kunden har inte betalat klart) hör inte till
    // någon lista. Databasen döljer dem redan för säljare och ansvariga.
    return data.filter(o => o.payment_status !== 'AWAITING_PAYMENT').map(mapOrderRow);
};

export const getCustomerOrders = async (email: string, phone: string): Promise<Order[]> => {
    // SECURE UPDATE: Use RPC function instead of direct table select
    // Direct select is now blocked by RLS for guests
    const { data, error } = await supabase.rpc('get_customer_orders_secure', {
        email_input: email,
        phone_input: phone
    });

    if (error) {
        console.error("Tracking error:", error);
        // Rate limiting i databasen ("För många försök") ska synas för kunden
        // istället för att se ut som "inga ordrar hittades".
        throw new Error(error.message || 'Kunde inte hämta beställningar just nu.');
    }
    if (!data) return [];
    
    return data.map(mapOrderRow);
};

export const submitClassOrders = async (classId: string) => {
    const { data: students } = await supabase.from('profiles').select('id').eq('class_id', classId);
    const studentIds = (students || []).map(s => s.id);
    
    let query = supabase
        .from('orders')
        .update({ status: 'SUBMITTED' })
        .eq('status', 'PENDING')
        // Ett kassaköp som kunden inte betalat klart skickas aldrig in (databasen döljer det redan för den ansvariga).
        .neq('payment_status', 'AWAITING_PAYMENT')
        .is('deleted_at', null);

    if (studentIds.length > 0) {
        query = query.or(`class_id.eq.${classId},and(class_id.is.null,student_id.in.(${studentIds.join(',')}))`);
    } else {
        query = query.eq('class_id', classId);
    }
    
    const { error } = await query;
    if (error) throw error;
};

export const confirmOrders = async (orderIds: string[]) => {
    const { error } = await supabase
        .from('orders')
        .update({ status: 'CONFIRMED' })
        .in('id', orderIds);
    if (error) throw error;
};

export const updateOrderPaymentStatus = async (orderId: string, status: 'PAID' | 'UNPAID') => {
    const { error } = await supabase
        .from('orders')
        .update({ payment_status: status })
        .eq('id', orderId);
    if (error) throw error;
};

export const deleteOrder = async (orderId: string) => {
    const { error } = await supabase
        .from('orders')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', orderId);
    if (error) throw error;
};

export const permanentDeleteOrder = async (orderId: string) => {
    // RLS ger inget fel när en rad inte får raderas — den raderas bara inte.
    // Be om de raderade raderna tillbaka så vi kan säga sanningen i UI:t.
    const { data, error } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderId)
        .select('id');
    if (error) throw error;
    if (!data || data.length === 0) {
        throw new Error('Ordern kunde inte raderas (endast administratörer kan radera permanent).');
    }
};

export const restoreOrder = async (orderId: string) => {
    const { error } = await supabase
        .from('orders')
        .update({ deleted_at: null })
        .eq('id', orderId);
    if (error) throw error;
};

export const getActionableOrderCount = async (user: User): Promise<number> => {
    let query = supabase.from('orders').select('*', { count: 'exact', head: true }).is('deleted_at', null);

    if (user.role === UserRole.ADMIN) {
        // Inskickade ordrar att fakturera + betalda hemleveranser att skicka. Obetalda kassaköp räknas inte.
        const { count: toShip } = await supabase.from('orders').select('*', { count: 'exact', head: true })
            .is('deleted_at', null).eq('delivery_method', 'HOME').eq('payment_status', 'PAID').is('shipped_at', null);
        const { count: submitted } = await query.eq('status', 'SUBMITTED').neq('payment_status', 'AWAITING_PAYMENT');
        return (submitted || 0) + (toShip || 0);
    } else if (user.role === UserRole.TEACHER && user.assignedClassId) {
        const { data: students } = await supabase.from('profiles').select('id').eq('class_id', user.assignedClassId);
        const ids = (students || []).map(s => s.id);
        
        if (ids.length > 0) {
            query = query.or(`class_id.eq.${user.assignedClassId},student_id.in.(${ids.join(',')})`);
        } else {
            query = query.eq('class_id', user.assignedClassId);
        }
        // Kassaköp skickas inte in av den ansvariga (betalda följer med automatiskt).
        query = query.eq('status', 'PENDING').neq('payment_method', 'STRIPE');
    } else {
        return 0;
    }

    const { count, error } = await query;
    return count || 0;
};

// --- Hemleveranser (admin) ---

const homeDeliveryAction = async (body: Record<string, unknown>): Promise<{ emailSent: boolean }> => {
    let response: Response;
    try {
        response = await apiFetch('/api/stripe', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });
    } catch {
        throw new Error('Kunde inte nå servern. Kontrollera din anslutning.');
    }
    const data = await response.json().catch(() => null);
    if (!response.ok) throw new Error(data?.error || 'Något gick fel.');
    return { emailSent: !!data?.emailSent };
};

/** Markerar hemleveransen som skickad. Kunden får mejlet "Ditt kaffe är på väg". */
export const shipHomeOrder = (orderId: string, trackingNumber?: string) =>
    homeDeliveryAction({ action: 'ship', orderId, trackingNumber });

/**
 * Fraktpriset för varukorgen vid hemleverans. Räknas i databasen
 * (home_shipping_quote), samma beräkning som när ordern läggs.
 */
export const quoteHomeShipping = async (items: { productId: string; variantId?: string; quantity: number; choices?: Record<string, string> }[]): Promise<ShippingQuote> => {
    const { data, error } = await supabase.rpc('quote_home_shipping', { p_items: items });
    if (error || !data) return { ok: false, reason: 'Fraktpriset kunde inte hämtas. Försök igen.' };
    return data.ok
        ? { ok: true, price: Number(data.price), weightGrams: Number(data.weightGrams) }
        : { ok: false, reason: String(data.reason || 'Hemleverans går inte att välja.') };
};

/** Admin: vägd vikt och mått efter packning. */
export const saveShippingWeighing = async (orderId: string, w: { grams: number | null; lengthCm: number | null; widthCm: number | null; heightCm: number | null }) => {
    const { error } = await supabase.from('orders').update({
        shipping_weighed_grams: w.grams,
        shipping_length_cm: w.lengthCm,
        shipping_width_cm: w.widthCm,
        shipping_height_cm: w.heightCm,
    }).eq('id', orderId);
    if (error) throw error;
};

/** Återbetalar en order som betalats i kassan (hemleverans eller hämtning) via Stripe. Ordern räknas då inte längre i gruppens försäljning. */
export const refundHomeOrder = (orderId: string) =>
    homeDeliveryAction({ action: 'refund', orderId });
